import { TooltipDirective } from '../../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../../componentes/botones';
import { Component, Inject, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { CategoriaGrupo } from 'modelos/src';
import { CardHeaderComponent } from '../../../../componentes/card-header/card-header.component';
import { GrupoSelectorComponent } from '../../../../componentes/selectores/grupo-selector/grupo-selector.component';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { GruposService } from '../../../../auxiliares/servicios/http/grupos.service';
import { AsignacionGrupoService } from './asignacion-grupo.service';
import { GrupoEntidadRegistry } from './grupo-entidad.registry';

interface ChipGrupo {
  nombre: string;
  color: string;
  esElegido: boolean;
}

export interface AsignarGrupoZonaData {
  categoria: CategoriaGrupo;
  polygon: GeoJSON.Polygon;
  idCliente?: string;
  etiqueta?: string; // "luminarias"
  // Allow-list de _id: si viene, solo se consideran las entidades del polígono
  // que estén en este set (las que quedaron tras aplicar los filtros del mapa).
  // Sin este campo se consideran todas las de adentro (comportamiento previo).
  idsFiltrados?: string[];
}

interface ItemSeleccion {
  entidad: Record<string, unknown>;
  label: string;
  sel: boolean;
}

/**
 * Dialog del asignador por polígono: lista las entidades de adentro con info
 * mínima y checkbox, y asigna el grupo elegido SOLO a las tildadas. Reusa
 * AsignacionGrupoService (mismo bulk + sync que el asignador por tabla).
 */
@Component({
  imports: [TooltipDirective, 
    FormsModule,
    BOTONES,
    MatIconModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatCardModule,
    CardHeaderComponent,
    GrupoSelectorComponent,
  ],
  selector: 'app-asignar-grupo-zona-dialog',
  templateUrl: './asignar-grupo-zona-dialog.component.html',
  styleUrl: './asignar-grupo-zona-dialog.component.scss',
})
export class AsignarGrupoZonaDialogComponent implements OnInit {
  private service = inject(AsignacionGrupoService);
  private registry = inject(GrupoEntidadRegistry);
  private gruposService = inject(GruposService);
  private ref = inject(MatDialogRef<AsignarGrupoZonaDialogComponent>);
  helper = inject(HelperService);

  public idGrupo: string | null = null;
  public items: ItemSeleccion[] = [];
  public cargando = true;
  public asignando = false;

  // nombre/color de los grupos de la categoría, para los chips del listado.
  private gruposPorId = new Map<string, { nombre?: string; color?: string }>();

  constructor(@Inject(MAT_DIALOG_DATA) public data: AsignarGrupoZonaData) {}

  async ngOnInit(): Promise<void> {
    const cfg = this.registry.get(this.data.categoria);
    try {
      await this.cargarNombresGrupos();
      let entidades = await this.service.entidadesEnPoligono(
        this.data.categoria,
        this.data.polygon,
      );
      // Respetar los filtros activos del mapa: descartar las de adentro que no
      // estén en la allow-list (si no vino, no se filtra nada).
      if (this.data.idsFiltrados) {
        const permitidos = new Set(this.data.idsFiltrados);
        entidades = entidades.filter((e) => permitidos.has(e['_id'] as string));
      }
      this.items = entidades.map((e) => ({
        entidad: e,
        label: cfg ? cfg.etiquetaEntidad(e) : ((e['_id'] as string) ?? ''),
        sel: true,
      }));
    } catch (e) {
      this.helper.notifError(e);
    } finally {
      this.cargando = false;
    }
  }

  private async cargarNombresGrupos(): Promise<void> {
    try {
      // Sin filtro por idCliente: un cliente padre puede ver luminarias de sus
      // hijos, cuyos grupos pertenecen a esos clientes hijos. includeChildren
      // trae también esos grupos → así chipsDe resuelve el nombre y no el _id.
      const res = await this.gruposService.getFiltered({
        filter: JSON.stringify({
          categoria: this.data.categoria,
        }),
        select: 'nombre color',
        unlimited: true,
        includeChildren: true,
      });
      for (const g of res.datos) {
        if (g._id)
          this.gruposPorId.set(g._id, { nombre: g.nombre, color: g.color });
      }
    } catch {
      // cosmético; si falla, no muestra chips
    }
  }

  // Chips de los grupos actuales de la entidad (resalta el grupo elegido).
  public chipsDe(entidad: Record<string, unknown>): ChipGrupo[] {
    const cfg = this.registry.get(this.data.categoria);
    const ids = cfg ? cfg.gruposDe(entidad) : [];
    return ids.map((id) => {
      const g = this.gruposPorId.get(id);
      return {
        nombre: g?.nombre ?? id,
        color: g?.color || '#9e9e9e',
        esElegido: id === this.idGrupo,
      };
    });
  }

  public get dentro(): number {
    return this.items.length;
  }
  public get seleccionadas(): number {
    return this.items.filter((i) => i.sel).length;
  }
  public get todasSel(): boolean {
    return this.items.length > 0 && this.items.every((i) => i.sel);
  }
  public toggleTodas(valor: boolean): void {
    for (const i of this.items) i.sel = valor;
  }

  public async aplicar(accion: 'asignar' | 'quitar'): Promise<void> {
    if (!this.idGrupo || !this.seleccionadas) return;
    this.asignando = true;
    try {
      const elegidas = this.items.filter((i) => i.sel).map((i) => i.entidad);
      const n = await this.service.bulkEntidades(
        this.data.categoria,
        this.idGrupo,
        elegidas,
        accion,
      );
      const verbo = accion === 'asignar' ? 'asignado a' : 'quitado de';
      this.helper.notifSuccess(
        `Grupo ${verbo} ${n} ${this.data.etiqueta ?? 'entidades'}`,
      );
      this.ref.close(n);
    } catch (e) {
      this.helper.notifError(e);
    } finally {
      this.asignando = false;
    }
  }

  public cerrar(): void {
    this.ref.close();
  }
}
